import { useCallback, useMemo, useRef, useState } from 'react'
import type { ComponentRef } from 'react'
import Box from '@mui/material/Box'
import InputAdornment from '@mui/material/InputAdornment'
import { Select as MbscSelect, setOptions, type MbscSelectItemData } from '@mobiscroll/react'
import '@mobiscroll/react/dist/css/mobiscroll.react.min.css'
import { UInput } from './UInput.component'
import './select-picker.css'

setOptions({ theme: 'ios', themeVariant: 'light' })

const DropdownArrow = () => (
    <svg width="17" height="9" viewBox="0 0 17 9" fill="none" data-testid="mobiscroll-select-svg">
        <path
            d="M16 1.55886L9.01234 8L7.9045 8L1 1.55629L1.56062 1.00257L8.23176 7.22737L8.68777 7.22737L15.4421 1L16 1.55886Z"
            fill="#99bcdf"
            stroke="#99bcdf"
            strokeWidth="0.3"
            data-testid="mobiscroll-select-path"
        />
    </svg>
)

export interface SelectPickerOption {
    text: string
    value: string
}

export interface SelectPickerProps {
    label?: string
    value: string
    data: SelectPickerOption[]
    onChange: (value: string) => void
    disabled?: boolean
    error?: boolean
    helperText?: string
    inputTestId?: string
    optionTestIdPrefix?: string
    'data-testid'?: string
}

type SelectPickerDataItem = SelectPickerOption & {
    testId: string
}

const toTestIdValue = (value: string, index: number) => {
    const normalized = value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')

    return normalized || `option-${index}`
}

export const SelectPicker = ({
    label,
    value,
    data,
    onChange,
    disabled,
    error,
    helperText,
    inputTestId,
    optionTestIdPrefix = 'mobiscroll-select',
    'data-testid': testId = 'mobiscroll-select'
}: SelectPickerProps) => {
    const ref = useRef<ComponentRef<typeof MbscSelect>>(null)
    const [isOpen, setIsOpen] = useState(false)
    const open = () => {
        if (!disabled) ref.current?.open()
    }
    const handleOpen = useCallback(() => setIsOpen(true), [])
    const handleClose = useCallback(() => setIsOpen(false), [])

    const displayText = value ? (data.find(d => d.value === value)?.text ?? '') : ''

    const dataWithTestIds = useMemo<SelectPickerDataItem[]>(
        () =>
            data.map((item, index) => ({
                ...item,
                testId: `${optionTestIdPrefix}-${toTestIdValue(item.text || item.value, index)}-option`
            })),
        [data, optionTestIdPrefix]
    )

    const renderItem = useCallback((item: MbscSelectItemData) => {
        const option = item.data as SelectPickerDataItem
        return <div data-testid={option.testId}>{option.text}</div>
    }, [])

    return (
        <Box sx={{ width: '100%', flex: 1, minWidth: 0 }} data-testid={`${testId}-container`}>
            {isOpen && (
                <Box
                    onClick={() => ref.current?.close()}
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 9998
                    }}
                    data-testid={`${testId}-backdrop`}
                />
            )}
            <Box sx={{ display: 'none' }} data-testid={`${testId}-mobiscroll-host`}>
                <MbscSelect
                    ref={ref}
                    data={dataWithTestIds}
                    value={value}
                    touchUi={false}
                    display="center"
                    filter={true}
                    buttons={[]}
                    itemHeight={45}
                    maxWidth={400}
                    filterPlaceholderText=""
                    showOverlay={true}
                    cssClass="select-picker-popup"
                    renderItem={renderItem}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    onChange={e => onChange((e.value as string) ?? '')}
                    data-testid={`${testId}-mobiscroll-select`}
                />
            </Box>
            <UInput
                label={label}
                value={displayText}
                onClick={open}
                disabled={disabled}
                error={error}
                helperText={helperText}
                fullWidth
                slotProps={{
                    htmlInput: {
                        readOnly: true,
                        ...(inputTestId ? { 'data-testid': inputTestId } : {})
                    },
                    input: {
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end" sx={{ pointerEvents: 'none' }} data-testid="mobiscroll-select-input-adornment">
                                <DropdownArrow data-testid="mobiscroll-select-dropdown-arrow" />
                            </InputAdornment>
                        )
                    },
                    inputLabel: { shrink: !!value }
                }}
                sx={{
                    cursor: disabled ? 'default' : 'pointer',
                    '& .MuiInputBase-input': {
                        cursor: disabled ? 'default' : 'pointer',
                        caretColor: 'transparent'
                    }
                }}
                data-testid="mobiscroll-select-uinput-open"
            />
        </Box>
    )
}
